<script setup>
import { ref, computed } from 'vue'
import { usePrestationStore } from '../../stores/prestationStore'

// Store
const prestationStore = usePrestationStore()

// Local state
const submitting = ref(false)
const error = ref(null)

// Computed
const isOpen = computed(() => prestationStore.toursModal.show)
const currentTours = computed(() => prestationStore.toursModal.currentTours)
const selectedTours = computed({
  get: () => prestationStore.toursModal.selectedTours,
  set: (value) => {
    prestationStore.toursModal.selectedTours = value
  }
})

const toursOptions = computed(() => {
  if (!prestationStore.toursModal.availableTours.length) return []
  
  return prestationStore.toursModal.availableTours
    .filter(tour => tour.count > currentTours.value) // Filtrer seulement les options supérieures aux tours actuels
    .map(tour => {
      const additionalTours = tour.count - currentTours.value
      const additionalPrice = tour.total_price - (prestationStore.toursModal.availableTours.find(t => t.count === currentTours.value)?.total_price || 0)
      
      let description = `+${additionalTours} tour${additionalTours > 1 ? 's' : ''} supplémentaire${additionalTours > 1 ? 's' : ''}`
      description += ` (total: ${tour.count} tours) - ${formatPrice(additionalPrice)}`
      
      return {
        value: tour.count,
        text: description,
        tour: tour,
        additionalTours: additionalTours,
        additionalPrice: additionalPrice
      }
    })
})

// Détermine si on utilise des possibilités prédéfinies ou un compteur libre
const usesPredefinedOptions = computed(() => {
  return prestationStore.toursModal.availableTours && prestationStore.toursModal.availableTours.length > 0
})

// Informations pour le mode compteur
const tourData = computed(() => {
  if (!prestationStore.toursModal.tourData) return null
  return prestationStore.toursModal.tourData
})

const canSubmit = computed(() => {
  return selectedTours.value > 0 && 
         selectedTours.value !== currentTours.value &&
         selectedTours.value > currentTours.value && // Strictement supérieur pour empêcher la diminution
         !submitting.value
})

const selectedTourInfo = computed(() => {
  return prestationStore.toursModal.availableTours.find(
    tour => tour.count === selectedTours.value
  )
})

const totalPrice = computed(() => {
  return selectedTourInfo.value ? selectedTourInfo.value.total_price : 0
})

const additionalPrice = computed(() => {
  if (usesPredefinedOptions.value) {
    // Mode avec possibilités prédéfinies
    const currentTour = prestationStore.toursModal.availableTours.find(
      tour => tour.count === currentTours.value
    )
    
    if (!currentTour || !selectedTourInfo.value) return 0
    
    return selectedTourInfo.value.total_price - currentTour.total_price
  } else {
    // Mode compteur libre
    if (!tourData.value) return 0
    
    const additionalTours = Math.max(0, selectedTours.value - currentTours.value)
    return additionalTours * tourData.value.montant_ttc_par_tour
  }
})

// Methods
function formatPrice(price) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price)
}

async function handleSubmit() {
  if (!canSubmit.value) return
  
  submitting.value = true
  error.value = null
  
  try {
    await prestationStore.updateTours(prestationStore.toursModal.prestationId, selectedTours.value)
    
    // Afficher un message de succès
    const alert = document.createElement('div')
    alert.className = 'alert alert-success alert-dismissible fade show position-fixed'
    alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999;'
    alert.innerHTML = `
      <i class="bi bi-check-circle me-2"></i>
      Tours mis à jour avec succès. ${selectedTours.value} tour${selectedTours.value > 1 ? 's' : ''} sélectionné${selectedTours.value > 1 ? 's' : ''}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    document.body.appendChild(alert)
    
    setTimeout(() => {
      alert.remove()
    }, 5000)
    
    handleCancel()
  } catch (err) {
    error.value = err.message || 'Erreur lors de la mise à jour des tours'
  } finally {
    submitting.value = false
  }
}

function handleCancel() {
  prestationStore.closeToursModal()
  error.value = null
}

function incrementTours() {
  if (!tourData.value) return
  if (selectedTours.value < tourData.value.nb_tour_max) {
    selectedTours.value++
  }
}

function decrementTours() {
  if (!tourData.value) return
  // Ne permettre de décrémenter que si on reste au-dessus du nombre de tours actuel
  if (selectedTours.value > currentTours.value) {
    selectedTours.value--
  }
}
</script>

<template>
  <!-- Modal -->
  <div v-if="isOpen" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-plus-circle me-2"></i>
            Ajouter des tours supplémentaires
          </h5>
          <button type="button" class="btn-close" @click="handleCancel"></button>
        </div>
        
        <div class="modal-body">
          <!-- Error Alert -->
          <div v-if="error" class="alert alert-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>
            {{ error }}
          </div>

          <!-- Loading State -->
          <div v-if="prestationStore.loading.tours" class="text-center py-3">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
            <p class="mt-2 mb-0">Chargement des options de tours...</p>
          </div>

          <!-- Content -->
          <div v-else>
            <div class="mb-3">
              <p class="text-muted mb-1">Tours actuels :</p>
              <p class="fw-bold">{{ currentTours }} tour{{ currentTours > 1 ? 's' : '' }}</p>
            </div>

            <!-- Tours Selection - Mode compteur libre -->
            <div v-if="!usesPredefinedOptions && tourData" class="mb-4">
              <label class="form-label">Nouveau nombre total de tours :</label>
              
              <div class="d-flex align-items-center gap-3 mb-3">
                <button 
                  type="button" 
                  class="btn btn-outline-secondary btn-sm"
                  @click="decrementTours"
                  :disabled="selectedTours <= currentTours"
                >
                  <i class="bi bi-dash"></i>
                </button>
                
                <div class="text-center">
                  <input 
                    v-model.number="selectedTours"
                    type="number" 
                    class="form-control text-center"
                    style="width: 80px;"
                    :min="currentTours + 1"
                    :max="tourData.nb_tour_max"
                  />
                </div>
                
                <button 
                  type="button" 
                  class="btn btn-outline-secondary btn-sm"
                  @click="incrementTours"
                  :disabled="selectedTours >= tourData.nb_tour_max"
                >
                  <i class="bi bi-plus"></i>
                </button>
              </div>

              <!-- Pricing Info for free counter mode -->
              <div class="p-3 bg-light rounded">
                <div class="row text-sm">
                  <div class="col-6">
                    <small class="text-muted">Prix base :</small><br>
                    <span class="fw-medium">{{ formatPrice(tourData.montant_ttc_base) }}</span>
                  </div>
                  <div class="col-6">
                    <small class="text-muted">Prix par tour supp. :</small><br>
                    <span class="fw-medium">{{ formatPrice(tourData.montant_ttc_par_tour) }}</span>
                  </div>
                </div>
                <hr class="my-2">
                <div class="d-flex justify-content-between">
                  <span>{{ selectedTours !== currentTours ? 'Supplément' : 'Prix actuel' }} :</span>
                  <span class="fw-bold" :class="selectedTours !== currentTours && additionalPrice > 0 ? 'text-warning' : 'text-muted'">
                    <template v-if="selectedTours !== currentTours">
                      {{ additionalPrice > 0 ? '+' : '' }}{{ formatPrice(additionalPrice) }}
                    </template>
                    <template v-else>
                      {{ formatPrice(tourData.montant_ttc_base) }}
                    </template>
                  </span>
                </div>
              </div>
            </div>

            <!-- Tours Selection - Mode possibilités prédéfinies -->
            <div v-else-if="usesPredefinedOptions && toursOptions.length" class="mb-4">
              <label for="toursSelect" class="form-label">Choisissez des tours supplémentaires :</label>
              
              <select 
                id="toursSelect"
                v-model="selectedTours"
                class="form-select mb-3"
                :disabled="submitting"
              >
                <option value="">Sélectionnez des tours supplémentaires</option>
                <option 
                  v-for="option in toursOptions" 
                  :key="option.value" 
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>

              <!-- Pricing Info for predefined options -->
              <div class="p-3 bg-light rounded">
                <div class="row text-sm">
                  <div class="col-12">
                    <small class="text-muted">Tours actuels :</small><br>
                    <span class="fw-medium">{{ currentTours }} tour{{ currentTours > 1 ? 's' : '' }}</span>
                  </div>
                  <div v-if="selectedTours" class="col-12 mt-2">
                    <small class="text-muted">Nouveau total après ajout :</small><br>
                    <span class="fw-medium">{{ selectedTours }} tour{{ selectedTours > 1 ? 's' : '' }}</span>
                  </div>
                </div>
                <hr class="my-2">
                <div class="d-flex justify-content-between">
                  <span>
                    <template v-if="selectedTours && selectedTours !== currentTours">
                      Coût des tours supplémentaires
                    </template>
                    <template v-else>
                      Sélectionnez une option
                    </template>
                  </span>
                  <span class="fw-bold" :class="selectedTours && selectedTours !== currentTours ? 'text-warning' : 'text-muted'">
                    <template v-if="selectedTours && selectedTours !== currentTours">
                      {{ formatPrice(additionalPrice) }}
                    </template>
                    <template v-else>
                      <span class="text-muted">-</span>
                    </template>
                  </span>
                </div>
              </div>
            </div>

            <!-- No options available or no additional tours possible -->
            <div v-if="!prestationStore.loading.tours && (!usesPredefinedOptions || toursOptions.length === 0) && !tourData" class="mb-4">
              <div class="text-center py-3">
                <i class="bi bi-info-circle text-muted" style="font-size: 2rem;"></i>
                <p class="text-muted mt-2">
                  <template v-if="usesPredefinedOptions && toursOptions.length === 0">
                    Vous avez déjà le maximum de tours disponibles pour cette prestation.
                  </template>
                  <template v-else>
                    Aucune option de tours supplémentaires disponible pour cette prestation.
                  </template>
                </p>
              </div>
              
              <!-- Empty pricing block to maintain modal size -->
              <div class="p-3 bg-light rounded">
                <div class="text-center text-muted">
                  <small>Les prix sont fixes pour cette prestation</small>
                </div>
              </div>
            </div>

            <!-- Warning for billing - always shown -->
            <div class="alert alert-warning">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Important :</strong> Toute modification sera facturée sur votre carte bancaire.
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="submitting">
            Annuler
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="!canSubmit || submitting"
            v-if="(usesPredefinedOptions && toursOptions.length > 0) || (!usesPredefinedOptions && tourData)"
          >
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ submitting ? 'Mise à jour...' : 'Ajouter les tours' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal {
  display: block !important;
}

.text-sm {
  font-size: 0.875rem;
}

.form-control[type="number"] {
  -moz-appearance: textfield;
}

.form-control[type="number"]::-webkit-outer-spin-button,
.form-control[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
